import React, { useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { isError } from "../../state/helpers/Error"
import Shortcut, { SHORTCUT_TYPE } from "../../state/shortcut/Shortcut"
import ShortcutService from "../../state/shortcut/ShortcutService"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import ShortcutsGrid from "./ShortcutsGrid"
import Icon from "../../common/icon/icon.component"
import ShortcutsSettingsModal from "../components/ShortcutsSettingsModal"
import DashboardLocationContent from "./DashboardLocationContent"

const ShortcutsContainer = () => {

    const [shortcutsFilter, setShortcutsFilter] = useState<Shortcut[]>([])
    const [shortcutsAction, setShortcutsAction] = useState<Shortcut[]>([])
    const [settingsModal, setSettingsModal] = useState(<></>)
    const [isEditable, setIsEditable] = useState<boolean>(false)
    const [locationId, setLocationId] = useState<string>("all")
    const [errorMsg, setErrorMsg] = useState<string>("")
    const isAdmin = AppContext.getUserLoggedIn().attributes.role.attributes.key === "admin"

    useEffect(() => {
        getAllShortcutFiltersAndDashboardFilters()
    }, [])

    const getAllShortcutFiltersAndDashboardFilters = () => {
        ShortcutService.retrieveAllShortcuts().then((r) => {
            if (!isError(r)) {
                setShortcutsFilter(r.filter(shortcut => shortcut.getShortcutType() === SHORTCUT_TYPE.FILTER))
                setShortcutsAction(r.filter(shortcut => shortcut.getShortcutType() === SHORTCUT_TYPE.ACTION))
            } else {
                setErrorMsg(r.message)
            }
        })
    }

    const triggerEdit = (event?: React.MouseEvent) => {
        event?.preventDefault()
        setIsEditable(true)
    }

    const saveShortcutRank = ({
        updatedShortcutsAction,
        updatedShortcutsFilter,
    }: { updatedShortcutsAction?: Shortcut[], updatedShortcutsFilter?: Shortcut[] }) => {
        const payload = {
            shortcutFiltersIds: [
                ...(updatedShortcutsAction || shortcutsAction),
                ...(updatedShortcutsFilter || shortcutsFilter),
            ].map(shortcutFilter => {
                return shortcutFilter.getId()
            })
        }

        ShortcutService.updateShortcutRank(payload).then(r => {
            if (isError(r)) {
                setErrorMsg(r.message)
            }
        })
    }

    const onClose = () => {
        setIsEditable(false)
    }

    const getShortcutsListByType = (shortcutType: SHORTCUT_TYPE) => {
        switch (shortcutType) {
        case SHORTCUT_TYPE.FILTER:
            return [...shortcutsFilter]
        case SHORTCUT_TYPE.ACTION:
            return [...shortcutsAction]
        default:
            return []
        }
    }

    const setShortcutsListByType = (shortcutType: SHORTCUT_TYPE, shortcuts: Shortcut[]) => {
        switch (shortcutType) {
        case SHORTCUT_TYPE.FILTER:
            setShortcutsFilter(shortcuts)
            break
        case SHORTCUT_TYPE.ACTION:
            setShortcutsAction(shortcuts)
            break
        }
    }


    const handleShortcuts = (shortcutType: SHORTCUT_TYPE, updatedShortcut: Shortcut, handleType: string) => {
        const shortcuts = getShortcutsListByType(shortcutType)
        let newShortcuts: Shortcut[] = []

        switch (handleType) {
        case "create":
            newShortcuts = shortcuts.concat(updatedShortcut)
            break
        case "update":
            newShortcuts = shortcuts.slice()
            for (const index in shortcuts) {
                if (shortcuts[index].getId() === updatedShortcut.getId()) {
                    newShortcuts[index] = updatedShortcut
                    break
                }
            }
            break
        case "delete":
            newShortcuts = shortcuts.filter(currentShortcut => currentShortcut.getId() !== updatedShortcut.getId())
            break
        }

        setShortcutsListByType(shortcutType, newShortcuts)
    }

    const handleLocationSelection = (locationId: string) => {
        const id = AppContext.getUserLoggedIn().id
        AppContext.getApiClient().patch("/users/" + id + "/preferences", {
            dashboardLocationId: locationId
        })
        setLocationId(locationId)
    }

    const openSettingsModal = (event: React.MouseEvent) => {
        event.preventDefault()
        setSettingsModal(
            <ShortcutsSettingsModal
                ranks={{
                    "shortcut-filter": shortcutsFilter.length,
                    "shortcut-action": shortcutsAction.length,
                }}
                onClose={closeSettingsModal}
                handleShortcutsList={handleShortcuts}
            />
        )
    }

    const closeSettingsModal = () => {
        setSettingsModal(<></>)
    }

    const getDashboardConfigureButtons = (isEditable: boolean) => {
        if (isEditable) {
            return <>
                <button className={"btn btn-sm btn-secondary padding-left-0-for-inner-span"} onClick={onClose}>
                    <Icon value={"times"} className={"mr-1"}/>
                    <FormattedMessage
                        id="dashboard.shortcut_filter.close_btn"
                        defaultMessage="Close"
                    />
                </button>
                <button className="btn btn-sm btn-primary" onClick={openSettingsModal}>
                    <Icon className={"mr-1"} value={"plus-square"}/>
                    <FormattedMessage
                        id="dashboard.shortcut_filter.add_tile_btn"
                        defaultMessage="Add a tile"
                    />
                </button>
            </>
        } else {
            return <button className={"btn btn-sm btn-link pr-0"} onClick={triggerEdit}>
                <small>
                    <Icon value={"cog"}/>
                </small>
                <FormattedMessage
                    id="dashboard.shortcut_filter.configure_btn"
                    defaultMessage="Configure"
                />
            </button>
        }
    }

    return (
        <div className="main-container mb-3">
            <div className={"col-12"}>

                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className={"mb-0 custom-lead text-secondary"}>
                            <FormattedMessage
                                id="dashboard.shortcut_filter.title"
                                defaultMessage="Hi {username}, here's what's new."
                                values={{username: AppContext.getUserLoggedIn().attributes.name}}
                            />
                        </h3>
                    </div>
                    <div className="d-flex align-items-center" style={{gap: "8px"}}>
                        {isAdmin && getDashboardConfigureButtons(isEditable)}
                        <DashboardLocationContent locationId={locationId}
                            handleLocationSelection={handleLocationSelection}/>
                    </div>
                </div>

                {/* This is a line */}
                <hr className="solid w-100"/>

                {/* Will display error message if there is a problem while fetching the shortcuts */}
                <div className="text-danger">{errorMsg}</div>

                {/* Shortcuts filters */}
                <ShortcutsGrid
                    isEditable={isEditable}
                    locationId={locationId}
                    shortcutsList={shortcutsFilter}
                    shortcutType={SHORTCUT_TYPE.FILTER}
                    onDragEnd={(shortcuts: Shortcut[]) => {
                        setShortcutsFilter(shortcuts)
                        saveShortcutRank({updatedShortcutsFilter: shortcuts})
                    }}
                    handleShortcutsList={handleShortcuts}
                />

                {/* Shortcuts actions */}
                <ShortcutsGrid
                    isEditable={isEditable}
                    locationId={locationId}
                    shortcutsList={shortcutsAction}
                    shortcutType={SHORTCUT_TYPE.ACTION}
                    onDragEnd={(shortcuts: Shortcut[]) => {
                        setShortcutsAction(shortcuts)
                        saveShortcutRank({updatedShortcutsAction: shortcuts})
                    }}
                    handleShortcutsList={handleShortcuts}
                    itemHeight={124}
                />
            </div>
            {settingsModal}
        </div>
    )
}

export default ShortcutsContainer


// WEBPACK FOOTER //
// ./src/shortcuts/sections/ShortcutsContainer.tsx